import { type CurvePoint, formatEUR } from '@budget/shared';
import { useId } from 'react';

const WIDTH = 340;
const HEIGHT = 150;
const PAD = { top: 14, right: 14, bottom: 22, left: 10 };

interface Props {
  points: CurvePoint[];
  /** Sert au libellé accessible ; la courbe elle-même n'affiche pas de valeur. */
  remainingCents: number;
}

function shortDate(iso: string): string {
  const [, month = '', day = ''] = iso.split('-');
  const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];
  return `${Number(day)} ${months[Number(month) - 1] ?? ''}`;
}

/**
 * Reste à vivre au fil du cycle.
 *
 * La trajectoire idéale est une LIGNE DE RÉFÉRENCE pointillée, pas une seconde
 * série : c'est une cible, pas un concurrent. Il n'y a donc qu'une couleur, et
 * pas de légende à lire — le pointillé porte son propre libellé.
 */
export function RemainingCurve({ points, remainingCents }: Props) {
  const baseId = useId();
  const aboveId = `${baseId}-above`;
  const belowId = `${baseId}-below`;
  if (points.length < 2) return null;

  const ideals = points.map((point) => point.idealCents);
  const actuals = points.flatMap((point) => (point.actualCents === null ? [] : [point.actualCents]));

  const yMax = Math.max(...ideals, ...actuals, 1);
  const yMin = Math.min(0, ...actuals);
  const span = yMax - yMin || 1;

  const plotWidth = WIDTH - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;

  const x = (index: number) => PAD.left + (index / (points.length - 1)) * plotWidth;
  const y = (value: number) => PAD.top + ((yMax - value) / span) * plotHeight;

  const idealPath = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${y(point.idealCents)}`).join(' ');

  // On conserve l'indice d'origine : les jours à venir n'ont pas de valeur
  // réelle, et la courbe doit s'arrêter à la bonne abscisse.
  const drawn = points
    .map((point, index) => ({ index, value: point.actualCents }))
    .filter((entry): entry is { index: number; value: number } => entry.value !== null);

  const actualPath = drawn
    .map((entry, order) => `${order === 0 ? 'M' : 'L'}${x(entry.index)},${y(entry.value)}`)
    .join(' ');

  const last = drawn.at(-1);
  const lastX = last === undefined ? PAD.left : x(last.index);
  const lastY = last === undefined ? y(0) : y(last.value);

  const zeroY = y(0);

  /*
   * L'aire se referme sur le ZÉRO, pas sur le bas du graphique. Fermée en bas,
   * elle s'étirait jusqu'au plancher dès qu'une valeur devenait négative — et
   * peignait un découvert en bleu.
   *
   * Le même tracé est peint deux fois, découpé au-dessus puis en dessous de la
   * ligne du zéro : inutile de calculer où la courbe la traverse.
   */
  const areaPath =
    last === undefined ? '' : `${actualPath} L${lastX},${zeroY} L${PAD.left},${zeroY} Z`;

  const bottom = HEIGHT - PAD.bottom;
  const aboveHeight = Math.max(0, Math.min(zeroY, bottom) - PAD.top);
  const belowHeight = Math.max(0, bottom - Math.max(zeroY, PAD.top));
  const goesNegative = yMin < 0;

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label={`Reste à vivre : ${formatEUR(remainingCents)}, du ${shortDate(points[0]!.date)} au ${shortDate(points.at(-1)!.date)}`}
    >
      <defs>
        <clipPath id={aboveId}>
          <rect x={PAD.left} y={PAD.top} width={plotWidth} height={aboveHeight} />
        </clipPath>
        <clipPath id={belowId}>
          <rect x={PAD.left} y={zeroY} width={plotWidth} height={belowHeight} />
        </clipPath>
      </defs>

      {/* Lavis à 10 % d'opacité, jamais un aplat. Bleu au-dessus du zéro,
          rouge en dessous : la couleur encode le signe de la valeur. */}
      {areaPath !== '' && (
        <>
          <path d={areaPath} fill="var(--series)" opacity="0.1" clipPath={`url(#${aboveId})`} />
          {goesNegative && (
            <path
              d={areaPath}
              fill="var(--series-negative)"
              opacity="0.14"
              clipPath={`url(#${belowId})`}
            />
          )}
        </>
      )}

      {/* Ligne du zéro, tracée APRÈS les aires pour rester lisible par-dessus. */}
      {goesNegative && (
        <line
          x1={PAD.left}
          x2={WIDTH - PAD.right}
          y1={zeroY}
          y2={zeroY}
          stroke="var(--axis)"
          strokeWidth="1"
        />
      )}

      {/* Trajectoire idéale : discrète, pointillée, sans couleur propre. */}
      <path
        d={idealPath}
        fill="none"
        stroke="var(--ink-muted)"
        strokeWidth="1.5"
        strokeDasharray="4 4"
        strokeLinecap="round"
      />

      {/* Même découpe pour le trait : il vire au rouge sous la ligne du zéro. */}
      <path
        d={actualPath}
        fill="none"
        stroke="var(--series)"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        clipPath={`url(#${aboveId})`}
      />
      {goesNegative && (
        <path
          d={actualPath}
          fill="none"
          stroke="var(--series-negative)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          clipPath={`url(#${belowId})`}
        />
      )}

      {/* Point de tête, cerclé de la couleur de surface pour rester lisible
          là où il croise le pointillé. */}
      {last !== undefined && (
        <circle
          cx={lastX}
          cy={lastY}
          r="4"
          fill={last.value < 0 ? 'var(--series-negative)' : 'var(--series)'}
          stroke="var(--surface)"
          strokeWidth="2"
        />
      )}

      <text
        x={WIDTH - PAD.right}
        y={PAD.top + 4}
        textAnchor="end"
        className="fill-ink-muted"
        fontSize="9"
      >
        trajectoire
      </text>

      <text x={PAD.left} y={HEIGHT - 6} className="fill-ink-muted tabular" fontSize="10">
        {shortDate(points[0]!.date)}
      </text>
      <text
        x={WIDTH - PAD.right}
        y={HEIGHT - 6}
        textAnchor="end"
        className="fill-ink-muted tabular"
        fontSize="10"
      >
        {shortDate(points.at(-1)!.date)}
      </text>
    </svg>
  );
}
